import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  adminSubscriptionService,
  type AdminSubscription,
} from '../../services/adminSubscriptionService';
import {
  adminTeamWorkspaceService,
  type TeamFollowupTask,
  type TeamSubscriptionRecord,
} from '../../services/adminTeamWorkspaceService';

const createTask = (): TeamFollowupTask => ({
  id: `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  title: '',
  dueDate: '',
  status: 'todo',
  priority: 'normal',
  notes: '',
  createdAt: new Date().toISOString(),
  completedAt: '',
});

const dateKey = (value?: string) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

const daysUntil = (value?: string) => {
  const key = dateKey(value);
  if (!key) return null;
  const target = new Date(`${key}T00:00:00.000Z`).getTime();
  const today = new Date();
  const todayKey = today.toISOString().slice(0, 10);
  const base = new Date(`${todayKey}T00:00:00.000Z`).getTime();
  return Math.round((target - base) / 86400000);
};

const TeamManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingInternalReminder, setSendingInternalReminder] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('');
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [records, setRecords] = useState<Record<string, TeamSubscriptionRecord>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [subs, rows] = await Promise.all([
          adminSubscriptionService.getSubscriptions(true),
          adminTeamWorkspaceService.getRecords(),
        ]);
        setSubscriptions(subs);
        setRecords(
          rows.reduce<Record<string, TeamSubscriptionRecord>>((acc, row) => {
            acc[row.subscriptionId] = row;
            return acc;
          }, {})
        );
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erreur chargement equipe');
      } finally {
        setLoading(false);
      }
    };
    load().catch(() => undefined);
  }, []);

  const allTasks = useMemo(() => {
    return Object.values(records).flatMap((record) =>
      record.tasks.map((task) => ({ ...task, subscriptionId: record.subscriptionId }))
    );
  }, [records]);

  const todayKey = new Date().toISOString().slice(0, 10);
  const todayTasks = allTasks.filter((task) => task.status !== 'done' && dateKey(task.dueDate) === todayKey);
  const overdueTasks = allTasks.filter((task) => task.status !== 'done' && task.dueDate && (daysUntil(task.dueDate) ?? 999) < 0);
  const renewalsSoon = subscriptions.filter((subscription) => {
    const diff = daysUntil(subscription.nextBillingDate);
    return diff !== null && diff >= 0 && diff <= 10;
  });
  const normalizedOwnerFilter = ownerFilter.trim().toLowerCase();
  const visibleSubscriptions = subscriptions.filter((subscription) => {
    if (!normalizedOwnerFilter) return true;
    const record = records[subscription.id];
    const ownerName = String(record?.ownerName || '').toLowerCase();
    const ownerEmail = String(record?.ownerEmail || '').toLowerCase();
    return ownerName.includes(normalizedOwnerFilter) || ownerEmail.includes(normalizedOwnerFilter);
  });

  const getRecord = (subscriptionId: string): TeamSubscriptionRecord =>
    records[subscriptionId] || {
      subscriptionId,
      ownerName: '',
      ownerEmail: '',
      updatedAt: '',
      tasks: [],
    };

  const patchRecord = (subscriptionId: string, patch: Partial<TeamSubscriptionRecord>) => {
    setRecords((current) => {
      const base = getRecord(subscriptionId);
      return {
        ...current,
        [subscriptionId]: {
          ...base,
          ...patch,
          subscriptionId,
          updatedAt: new Date().toISOString(),
        },
      };
    });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const list = Object.values(records).filter(
        (record) =>
          record.subscriptionId &&
          (record.ownerName.trim() || record.ownerEmail.trim() || record.tasks.length > 0)
      );
      const saved = await adminTeamWorkspaceService.saveRecords(list);
      setRecords(
        saved.reduce<Record<string, TeamSubscriptionRecord>>((acc, row) => {
          acc[row.subscriptionId] = row;
          return acc;
        }, {})
      );
      toast.success('Assignations et taches sauvegardees');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erreur sauvegarde equipe');
    } finally {
      setSaving(false);
    }
  };

  const sendInternalReminderNow = async () => {
    setSendingInternalReminder(true);
    try {
      const response = await fetch('/api/admin/team-reminders', {
        method: 'POST',
        credentials: 'include',
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || 'Rappel equipe impossible');
      }
      toast.success(`Rappels equipe: ${payload.sent || 0} envoyes, ${payload.failed || 0} echec(s)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rappel equipe impossible');
    } finally {
      setSendingInternalReminder(false);
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-gray-600">Chargement espace equipe...</div>;
  }

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900">Vue Aujourd hui equipe</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={sendInternalReminderNow}
              disabled={sendingInternalReminder}
              className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60"
            >
              {sendingInternalReminder ? 'Envoi...' : 'Envoyer rappel equipe'}
            </button>
            <button
              type="button"
              onClick={saveAll}
              disabled={saving}
              className="rounded-lg bg-neon-orange px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {saving ? 'Sauvegarde...' : 'Enregistrer les assignations'}
            </button>
          </div>
        </div>
        <div className="mt-3 max-w-sm">
          <label className="mb-1 block text-sm font-medium text-gray-700">Mon portefeuille</label>
          <input
            type="text"
            value={ownerFilter}
            onChange={(event) => setOwnerFilter(event.target.value)}
            placeholder="Filtrer par responsable (nom ou email)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2"
          />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Taches du jour</div>
            <div className="mt-1 text-3xl font-bold text-indigo-700">{todayTasks.length}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Taches en retard</div>
            <div className="mt-1 text-3xl font-bold text-red-600">{overdueTasks.length}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Renouvellements sous 10 jours</div>
            <div className="mt-1 text-3xl font-bold text-emerald-700">{renewalsSoon.length}</div>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        {visibleSubscriptions.map((subscription) => {
          const record = getRecord(subscription.id);
          return (
            <article key={subscription.id} className="rounded-2xl border border-gray-200 bg-white p-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-400">Abonnement</div>
                  <div className="text-lg font-semibold text-gray-900">{subscription.subscriptionNumber}</div>
                  <div className="text-sm text-gray-600">{subscription.clientName} · {subscription.serviceName}</div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Responsable compte</label>
                  <input
                    type="text"
                    value={record.ownerName}
                    onChange={(event) => patchRecord(subscription.id, { ownerName: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="Nom responsable"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Email responsable</label>
                  <input
                    type="email"
                    value={record.ownerEmail}
                    onChange={(event) => patchRecord(subscription.id, { ownerEmail: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                    placeholder="responsable@agence.com"
                  />
                </div>
              </div>

              <div className="mt-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900">Taches de suivi</h4>
                  <button
                    type="button"
                    onClick={() => patchRecord(subscription.id, { tasks: [...record.tasks, createTask()] })}
                    className="rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-indigo-700 border border-indigo-200"
                  >
                    + Ajouter tache
                  </button>
                </div>
                {record.tasks.length === 0 ? (
                  <p className="text-sm text-gray-500">Aucune tache pour cet abonnement.</p>
                ) : (
                  <div className="space-y-2">
                    {record.tasks.map((task) => (
                      <div key={task.id} className="grid gap-2 md:grid-cols-[1.6fr_130px_120px_110px_42px]">
                        <input
                          type="text"
                          value={task.title}
                          onChange={(event) =>
                            patchRecord(subscription.id, {
                              tasks: record.tasks.map((entry) =>
                                entry.id === task.id ? { ...entry, title: event.target.value } : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                          placeholder="Ex: Relance paiement / point performance"
                        />
                        <input
                          type="date"
                          value={dateKey(task.dueDate)}
                          onChange={(event) =>
                            patchRecord(subscription.id, {
                              tasks: record.tasks.map((entry) =>
                                entry.id === task.id
                                  ? {
                                      ...entry,
                                      dueDate: event.target.value
                                        ? new Date(`${event.target.value}T12:00:00`).toISOString()
                                        : '',
                                    }
                                  : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        />
                        <select
                          value={task.priority}
                          onChange={(event) =>
                            patchRecord(subscription.id, {
                              tasks: record.tasks.map((entry) =>
                                entry.id === task.id
                                  ? { ...entry, priority: event.target.value as TeamFollowupTask['priority'] }
                                  : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        >
                          <option value="low">Basse</option>
                          <option value="normal">Normale</option>
                          <option value="high">Haute</option>
                        </select>
                        <select
                          value={task.status}
                          onChange={(event) =>
                            patchRecord(subscription.id, {
                              tasks: record.tasks.map((entry) =>
                                entry.id === task.id
                                  ? {
                                      ...entry,
                                      status: event.target.value as TeamFollowupTask['status'],
                                      completedAt:
                                        event.target.value === 'done' ? new Date().toISOString() : '',
                                    }
                                  : entry
                              ),
                            })
                          }
                          className="rounded-lg border border-gray-300 px-3 py-2"
                        >
                          <option value="todo">A faire</option>
                          <option value="done">Fait</option>
                        </select>
                        <button
                          type="button"
                          onClick={() =>
                            patchRecord(subscription.id, {
                              tasks: record.tasks.filter((entry) => entry.id !== task.id),
                            })
                          }
                          className="rounded-lg border border-red-200 text-red-600 bg-white"
                          title="Supprimer la tache"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default TeamManager;
